import { Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';

@Component({
  selector: 'app-property-buyers',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './property-buyers.component.html',
  styleUrl: './property-buyers.component.css',
})
export class PropertyBuyersComponent implements OnChanges, OnDestroy {
  @Input() propertyId: string = '';

  buyers: any[] = [];
  showForm: boolean = false;
  editingUid: string | null = null;
  buyerForm!: FormGroup;

  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private fb = inject(FormBuilder);
  private alertService = inject(NgxToastrService);

  private subscriptions = new Subscription();
  private loadedFor: string = '';

  constructor() {
    this.initForm();
  }

  ngOnChanges(): void {
    // Load once we have a propertyId (the parent may set it asynchronously).
    if (this.propertyId && this.propertyId !== this.loadedFor) {
      this.loadedFor = this.propertyId;
      this.loadBuyers();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initForm(): void {
    this.buyerForm = this.fb.group({
      buyer_name: ['', Validators.required],
      email: ['', Validators.email],
      phone: [''],
      amount: [''],
      purchase_date: [''],
      address: [''],
      id_proof_type: [''],
      id_proof_number: [''],
      notes: [''],
    });
  }

  loadBuyers(): void {
    if (!this.propertyId) return;
    const sub = this.httpService.getPropertyBuyers(this.propertyId).subscribe({
      next: (response) => {
        if (response.success) this.buyers = response.data ?? [];
      },
    });
    this.subscriptions.add(sub);
  }

  openAdd(): void {
    this.editingUid = null;
    this.buyerForm.reset();
    this.showForm = true;
  }

  openEdit(buyer: any): void {
    this.editingUid = buyer.uid;
    this.buyerForm.reset();
    this.buyerForm.patchValue({
      buyer_name: buyer.buyer_name ?? '',
      email: buyer.email ?? '',
      phone: buyer.phone ?? '',
      amount: buyer.amount ?? '',
      purchase_date: buyer.purchase_date ?? '',
      address: buyer.address ?? '',
      id_proof_type: buyer.id_proof_type ?? '',
      id_proof_number: buyer.id_proof_number ?? '',
      notes: buyer.notes ?? '',
    });
    this.showForm = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingUid = null;
    this.buyerForm.reset();
  }

  submitBuyer(): void {
    if (this.buyerForm.invalid) {
      this.buyerForm.markAllAsTouched();
      return;
    }
    // Strip empty strings so optional fields are sent as omitted, not "".
    const raw = this.buyerForm.value;
    const payload: any = {};
    Object.keys(raw).forEach((k) => {
      if (raw[k] !== null && raw[k] !== '') payload[k] = raw[k];
    });

    this.loadingService.show();
    const call = this.editingUid
      ? this.httpService.updatePropertyBuyer(payload, this.propertyId, this.editingUid)
      : this.httpService.addPropertyBuyer(payload, this.propertyId);

    const sub = call.subscribe({
      next: (response) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message, 'toast-top-right');
          this.cancelForm();
          this.loadBuyers();
        } else {
          this.alertService.error(response.message, 'toast-top-right');
        }
      },
      error: () => this.loadingService.hide(),
    });
    this.subscriptions.add(sub);
  }

  deleteBuyer(uid: string): void {
    const sub = this.httpService.deletePropertyBuyer(uid, this.propertyId).subscribe({
      next: (response) => {
        if (response.success) {
          this.alertService.success(response.message, 'toast-top-right');
          this.loadBuyers();
        }
      },
    });
    this.subscriptions.add(sub);
  }

  get canAdd(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.ADD_BUYER_DETAILS);
  }
  get canEdit(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.EDIT_BUYER_DETAILS);
  }
  get canDelete(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.PROPERTIES.DELETE_BUYER_DETAILS);
  }
}
